import { payment_status } from "../pages/order-history/fetch/paymentMethod";
import { AlertType, SalesStatus } from "./enums";
import { Branch, Brand, FreightOption, Size } from "./types";

export interface Address {
  id?: number;
  ship_to_name: string;
  address_line1: string;
  address_line2: string;
  address_line3?: string;
  address_line4?: string;
  address_state?: string;
  state?: string;
  address_city: string;
  address_country: string;
  address_postcode: string;
  is_primary_address?: boolean;
  address_contact?: string | null;
  address_fax?: string | null;
  address_phone?: string | null;
  ship_code?: string | null;
  dpid?: number | null;
}

export interface AddressPrimary {
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  addressLine4: string;
  city: string;
  country: string;
  description: string;
  isActive: boolean;
  isDefaultShipToLocation: boolean;
  isPrimary: true;
  locationCode: string;
  postCode: string;
  shipToName: string;
  state: string;
}

export interface Product {
  brand: Brand;
  code: string;
  colors: any[];
  id: number;
  images: any[];
  index: number;
  name: string;
  original_brand: string;
  public_price?: number | null;
  sales_status: SalesStatus;
  slug: string;
  subdomain_price?: number | null;
  tags: string[];
  total_colors: number;
  prices?: ProductPriceWithTier[];
  badge_info?: string;
}

export type ProductWithoutIndex = Omit<Product, "index">;

type CompanionProductBase = Omit<Product, "index">;

export interface CompanionProduct extends CompanionProductBase {
  prices: ProductPriceWithTier[];
}

export interface OrderDetailItem {
  id: number;
  product_id: number;
  order_id: number;
  quantity: number;
  fulfilled_quantity: number;
  backorder: Warehouse | null;
  warehouses: Warehouse[] | null;
  color: string;
  size: Size;
  price: string;
  sku: string;
  brand: Brand;
  product: string;
  product_code: string;
  product_name: string;
  formatted_item_no: string;
  reference: string | null;
  color_images: ProductImage[];
  stock: Stock[];
  eta: any | null;
  images: ProductImage[];
  promotion_code: string | null;
  enable_ship_from_au: boolean | null;

  // Index is a field manually added later to keep order of items
  index?: string;
}

export interface OrderItem extends OrderDetailItem {
  available_quantity: number;
  available_price: number;
  backorder: Warehouse | null;
  // This field is only here when called from '/load_items_detail' and replaces brand
  brand_name?: string;
  color: string;
  eta: any | null;
  formatted_item_no: string;
  id: number;
  // Index field doesn't normally exist on this, but is added in OrderUploader
  index?: string;
  itemNo?: string | number;
  order_id: number;
  price: string;
  product: string;
  product_code: string;
  product_id: number;
  product_name: string;
  promotion_code: string | null;
  quantity: number;
  reference: string | null;
  sku: string;
  stock: Stock[];
  warehouses: Warehouse[];
  po_number: string;
  ship_to_address?: string;
  status?: string;
  submitted_at?: string;
  tax_amount: number;
  tax_rate: string;
  total_amount: number;
  total_amount_after_tax: string;
  total_amount_before_tax: string;
  total_items: number;
  total_units: number;
  track_numbers: any[];
  transaction_fee: string;
  grand_total: string;
}

export interface Stock {
  location: string;
  qtyAvailable: number;
}

interface Warehouse {
  location: string;
  quantity: string;
}

export interface ProductImage {
  // inconsistent back-end so it could return either front or front_image
  front?: boolean;
  front_image?: boolean;
  https_attachment_url: string;
  https_attachment_url_product: string;
  id: number;
  image_type?: string;
  index?: number;
}

export interface ProductColor {
  hex_value?: string;
  id: number;
  images: ProductImage[];
  index: number;
  name: string;
  sizes: any[];
  status: string;
  tag_name: string;
  tag_name_alt?: string;
  tag_value: string;
  tag_value_alt?: string;
  enable_ship_from_au: boolean;
}

export interface ProductDescription {
  cares?: string[];
  extras?: string[];
  features?: string[];
  fabric?: any;
  sizes?: any;
  product_detail_message?: string;
}

export interface ProductPriceWithTier {
  tier: string;
  price: number;
}

interface ProductListLinks {
  next: string | null;
  previous: string | null;
}

export interface ProductListAPIResponse {
  current: number;
  links: ProductListLinks;
  next: number | null;
  previous: number | null;
  products: Product[];
  total_count: number;
}

interface SizeDetail {
  key: string;
  value: string;
  position: number;
}

export interface SizeChart {
  id: number;
  measurement: string;
  size_details: SizeDetail[];
}

export interface ProductDetailResponse {
  brand: Brand;
  catwalk_url?: string;
  code: string;
  colors: any[];
  companions: CompanionProduct[];
  description?: ProductDescription;
  fabric?: string | null;
  fit?: string;
  gender: string;
  id: number;
  images: ProductImage[];
  index: number;
  industry?: string | null;
  name: string;
  original_brand: string;
  pictograms: any[];
  prices?: ProductPriceWithTier[];
  public_price?: number;
  sales_status: SalesStatus;
  seo_focuskw?: string;
  seo_metadesc?: string;
  seo_score?: string | null;
  seo_title?: string;
  size_charts?: SizeChart[];
  sleeve?: string;
  slug: string;
  stylesheet?: string;
  subdomain_price?: string | null;
  suggestions: any[];
  tags?: string[];
  tech?: string | null;
  badge_info?: string;
}

export interface TrackingInformation {
  location: string;
  trackingNumber: string;
  trackingUrl: string;
}

export interface OrderDetail {
  admin_order_id: string | null;
  calculated_at: number;
  comment: string | null;
  courier: string | null;
  customer: number;
  discount_by_freight: number;
  discount_by_freight_amount: number;
  distributor_state: string;
  freight_option: FreightOption | null;
  has_promotion_item: boolean;
  id: number;
  is_admin_order: boolean | null;
  note: string | null;
  order_id: string | null;
  // order_items doesn't really have all the properties of OrderItem but mostly re-usable
  // If you want to check and compare - https://i.imgur.com/yAHeIiA.png
  order_items: OrderDetailItem[];
  po_number: string;
  ship_to_address: string | null;
  status: string;
  submitted_at: string | null;
  tax_amount: number;
  tax_rate: string;
  total_amount: number;
  total_items: number;
  total_units: number;
  track_numbers: TrackingInformation[];
  payment_method: IPaymentMethod;
  payment_status: payment_status;
  payment_details: IPaymentDetails;
  payment_details_paid: IPaymentDetails;
}

export interface IPaymentDetails {
  total_products: number;
  shipping_cost: number;
  tax: number;
  transaction_fee: number;
  subtotal: number;
  grand_total: number;
}

export type IPaymentMethod = "online" | "bank_transfer" | "credit_limit" | "email_transfer";

export enum PaymentMethods {
  Online = "online",
  BankTransfer = "bank_transfer",
  CreditLimit = "credit_limit",
  EmailTransfer = "email_transfer",
}

export interface AlertContent {
  type: AlertType;
  message: string;
}

export interface OrderSplitResultItem {
  backOrder: Warehouse | null;
  color: string;
  error: string;
  fulfilledQuantity: string;
  id: string;
  // index property will NEVER exist on this type, but is here due to a TypeScript error in CartItemTable preventing from being compiled
  // This is a result of bad practice and if it could be fixed later after removing this unused property, then all will be good.
  index?: string;
  itemNo: string;
  price: string;
  quantity: string;
  reference: string;
  product_name?: string;
  size: string;
  sku: string;
  totalWeight: string;
  unitWeight: string;
  warehouses: Warehouse[];
}

export interface OrderSplitCurrentOrder {
  id: number;
  order_id: number | null;
  status: string | null;
  track_numbers: any[];
  customer: number;
  order_items: OrderDetailItem[];
  tax_rate: string;
  total_items: number;
  total_units: number;
  distributor_state: string;
  note: string | null;
  po_number: string;
  freight_option: FreightOption | null;
  courier: string | null;
  ship_to_address: string | null;
  comment: string | null;
  discount_by_freight: number;
  discount_by_freight_amount: number;
  tax_amount: number;
  total_amount: number;
  submitted_at: number | string | null;
  calculated_at: number;
  has_promotion_item: boolean;
  is_admin_order: boolean | null;
  admin_order_id: string | null;
  payment_method: IPaymentMethod;
  payment_details: IPaymentDetails;
}

export interface OrderSplitAPIResponse {
  current_order: OrderSplitCurrentOrder;
  result: {
    address_city: string | null;
    address_contact: string | null;
    address_country: string;
    address_fax: string | null;
    address_line1: string;
    address_line2: string;
    address_line3: string;
    address_line4: string;
    address_phone: string | null;
    address_postcode: string;
    address_state: string;
    comment: string | null;
    courier: string | null;
    cubicMeter: string | null;
    customer_id: string;
    discount_by_freight: number;
    freight_fee: number;
    freight_option: string;
    is_primary_address: boolean;
    items: OrderSplitResultItem[];
    location: string;
    note: string;
    order_rails_id: string;
    order_web_no: string | null;
    po_number: string;
    release_stock_id: string;
    ship_code: string | null;
    ship_to_name: string | null;
    ship_via: string;
    tax_rate: string;
    totalWeight: string;
  };
}

export interface OrderUploadItemDetailsResponse {
  backorder_quantity: string;
  brand_name: string;
  color: string;
  customer_id: string;
  eta: { eta: string; quantity: number }[];
  images: ProductImage[];
  index: string;
  itemNo: string;
  price: string;
  product_code: string;
  product_id: number;
  product_name: string;
  product_slug: string;
  promotion_code: string;
  quantity: string;
  reference: string;
  size: string;
  sku: string;
  stock: Stock[];
  csvLineArray: string[];
  error_code?: string;
}

export interface TransactionSummary {
  credit: string;
  date: string;
  debit: string;
  orderNo: string;
  status: string;
  transactionNumber: string;
  type: string;
}

interface CustomerAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  country: string;
  name: string;
  state: string;
  suburb: string;
}

interface CustomerBalance {
  gst: string;
  less_disc: string;
  sub_total: string;
  total: string;
  total_misc: string;
}

interface TransactionItem {
  extPrice: string;
  itemCode: string;
  itemDesc: string;
  qty: string;
  unitPrice: string;
}

export interface TransactionDetails {
  CustomerAddress: CustomerAddress;
  balance: CustomerBalance;
  comment: string;
  credit_note_no: string;
  customer_accpac_no: string;
  date: string;
  items: TransactionItem[];
  misc: string | null;
  note: string;
  original_inv_no: string;
  ran: string;
  ret_doc_no: string;
  shipping: any | null;
}

export interface LoginDetails {
  allow_webstore: boolean;
  branch: Branch;
  brands: string[];
  company_accpac_no: string;
  country: string;
  customer_id: number;
  email: string;
  features: string[];
  jwt: string;
  organization_key: string;
  token: string;
  webstore_admin: boolean;
  webstore_order_enabled: true;
  remember_me?: boolean;
  stocklist_mode: boolean;
  is_cod: boolean;
}
